import { useCallback, useEffect, useState } from "react";
import { Pin, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useMarket, fmtDate } from "@/lib/market";
import { useSitePage, Hero, IMAGES, media } from "@/components/public/Blocks";

const NETWORKS = [["", "Tout"], ["instagram", "Instagram"], ["x", "X"], ["tiktok", "TikTok"], ["youtube", "YouTube"], ["manual", "Ma Petite Ligue"]];
const NET_LABEL = Object.fromEntries(NETWORKS);

const igEmbed = (url) => { const m = url?.match(/instagram\.com\/(p|reel)\/([A-Za-z0-9_-]+)/); return m ? `https://www.instagram.com/${m[1]}/${m[2]}/embed` : null; };

export function FeedCard({ item }) {
  const embed = !item.media_url && item.network === "instagram" ? igEmbed(item.url) : null;
  return (
    <article className={`p-surface flex flex-col overflow-hidden ${item.featured ? "sm:col-span-2" : ""}`} data-testid={`feed-item-${item.id}`}>
      {item.media_url && <img src={media(item.media_url)} alt={item.caption?.slice(0, 80) || ""} loading="lazy" className="aspect-square w-full object-cover" />}
      {embed && <iframe src={embed} title="Publication Instagram" loading="lazy" className="aspect-[4/5] w-full border-0 bg-white" />}
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex flex-wrap items-center gap-2 text-xs p-t3">
          <span className="p-chip">{NET_LABEL[item.network]}</span>{item.pinned && <Pin size={13} aria-label="Épinglé" />}{item.category && <span>{item.category}</span>}
          <span className="ml-auto">{fmtDate(item.posted_at)}</span></div>
        {item.caption && <p className="line-clamp-4 whitespace-pre-line text-sm p-t2">{item.caption}</p>}
        {item.url && <a href={item.url} target="_blank" rel="noopener noreferrer" className="mt-auto inline-flex items-center gap-1 text-xs font-bold p-accent" data-testid={`feed-link-${item.id}`}>Voir la publication <ExternalLink size={12} /></a>}
      </div>
    </article>
  );
}

export default function FeedPage() {
  const blocks = useSitePage("feed");
  const [market] = useMarket();
  const [net, setNet] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const load = useCallback((offset = 0) => {
    api.get("/feed", { params: { market, network: net || undefined, limit: 12, offset } })
      .then(({ data: d }) => setData((p) => ({ items: offset ? [...p.items, ...d.items] : d.items, total: d.total }))).catch(() => {});
  }, [market, net]);
  useEffect(() => { load(0); }, [load]);
  return (
    <div data-testid="feed-page">
      <Hero block={blocks?.find((b) => b.key === "hero")} testId="feed-hero" />
      <section className="mx-auto max-w-7xl px-5 py-12">
        <div className="mpl-scroll-x flex gap-2 overflow-x-auto" role="tablist">{NETWORKS.map(([k, l]) => (
          <button key={k} role="tab" aria-selected={net === k} onClick={() => setNet(k)} className={`p-chip shrink-0 !py-2 !text-sm ${net === k ? "p-chip-live" : ""}`} data-testid={`feed-filter-${k || "all"}`}>{l}</button>))}</div>
        {data.items.length ? <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{data.items.map((i) => <FeedCard key={i.id} item={i} />)}</div>
          : <p className="mt-8 text-base p-t3" data-testid="feed-empty">Aucune publication pour le moment. Suis @mapetiteligue sur nos réseaux en attendant.</p>}
        {data.items.length < data.total && <button onClick={() => load(data.items.length)} className="p-btn-ghost mt-8" data-testid="feed-more">Voir plus</button>}
      </section>
    </div>
  );
}
